/**
 * 🔒 安全日志工具
 * 在生产环境中过滤敏感信息
 */

// 检查是否为开发环境
const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';

/**
 * 🔒 脱敏处理（隐藏敏感信息）
 * @param {any} data - 要脱敏的数据
 * @returns {any} 脱敏后的数据
 */
function sanitize(data) {
  if (typeof data === 'string') {
    let sanitized = data;
    // 隐藏十六进制数据（可能是密钥）
    sanitized = sanitized.replace(/\b[0-9A-Fa-f]{32,}\b/g, (match) => {
      return match.substring(0, 8) + '...' + match.substring(match.length - 4);
    });
    return sanitized;
  }
  
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(sanitize);
    }
    
    const sanitized = {};
    for (const key in data) {
      // 跳过敏感字段
      if (/pin|password|secret|token|key/i.test(key)) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitize(data[key]);
      }
    }
    return sanitized;
  }
  
  return data;
}

/**
 * 安全的 console.log（开发环境显示全部，生产环境过滤）
 */
export const logger = {
  /**
   * 调试信息（仅开发环境）
   */
  debug(...args) {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },
  
  /**
   * 普通日志（生产环境脱敏）
   */
  log(...args) {
    if (isDev) {
      console.log(...args);
    } else {
      // 生产环境：脱敏处理
      console.log(...args.map(sanitize));
    }
  },
  
  /**
   * 警告信息（始终显示，但脱敏）
   */
  warn(...args) {
    if (isDev) {
      console.warn(...args);
    } else {
      console.warn(...args.map(sanitize));
    }
  },
  
  /**
   * 错误信息（始终显示，但隐藏内部细节）
   */
  error(...args) {
    if (isDev) {
      console.error(...args);
    } else {
      // 生产环境：只显示错误类型，不显示详细堆栈
      const sanitizedArgs = args.map(arg => {
        if (arg instanceof Error) {
          return {
            message: arg.message,
            name: arg.name,
            // 不包含 stack，防止信息泄露
          };
        }
        return sanitize(arg);
      });
      console.error(...sanitizedArgs);
    }
  },
  
  /**
   * 性能日志（仅开发环境或重要警告）
   */
  perf(message, timeMs, threshold = 500) {
    if (timeMs > threshold) {
      if (isDev) {
        console.warn(`🐌 ${message}: ${timeMs.toFixed(1)}ms`);
      } else {
        // 生产环境：只在超过2倍阈值时显示
        if (timeMs > threshold * 2) {
          console.warn(`⚠️ Slow operation: ${timeMs.toFixed(0)}ms`);
        }
      }
    } else if (isDev) {
      console.log(`✓ ${message}: ${timeMs.toFixed(1)}ms`);
    }
  },
  
  /**
   * 用户操作日志（用于审计，生产环境记录）
   */
  audit(action, details = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      action,
      ...sanitize(details),
    };
    
    if (isDev) {
      console.log('[AUDIT]', logEntry);
    } else {
      // 生产环境：可以发送到服务器或本地存储
      console.log('[AUDIT]', logEntry);
    }
  },
};

// 导出默认
export default logger;

